/**
 * 設定 → {order} /settings/orders（#233、docs/93 §4.6、Figma ③・③-2）。設定のメニューの節「見積・請求」の見積依頼の下。書けるのは管理者だけ（自動保存）
 *   名称（帳票を選ぶと請書に対の名前・請書は手で直せる）・電子承諾・約款（標準（国交省）／自社の PDF／添付しない＝2c）・印刷で約款（既定オフ）・
 *   変更協議の定型文・印字項目（内訳表・社印）・{paymentNotice}（名称＝支払通知書⇄支払明細書・確認の期限＝送付後 n日。#233 3b）
 *   本人確認（SMS 確認コード・金額 ○円以上）は SMS の送り口がまだ無いので出さない（2回目以降＝Figma に札「2回目以降」）
 *   スイッチ・選ぶ欄は押した瞬間に保存、打つ欄は欄を離れたとき（設定の画面の決まり＝S-00008）
 *   #250〜#255（docs/93 §16）：約款の節は field の会社（現場力）だけ・約款「添付しない」の注意文はやめた
 *   #256・#261（docs/93 §17、Figma ① 255:57）：
 *     ・節「{orderAck}」＝頭のスイッチ（既定オフ＝ackMode none）。オンのときだけ 受け取り方（電子承諾｜書面。オンにした瞬間は書面）・本人確認（電子承諾のとき＝リンクのみ）。
 *       {order}兼請書の行はオン・オフに関わらず出す（オフで隠すとオンにできなかった）
 *     ・節「約款」＝頭のスイッチ（既定オフ＝termsKind none）。オンのときだけ 種類（標準（国交省）｜自社の PDF）・約款 PDF・印刷で約款・変更協議（オンにした瞬間はオン）・定型文（押すと文の窓）。
 *       約款をオフにすると変更協議もオフで保存
 */
import { ORDER_ACK_LABEL, ORDER_ACK_RECEIVE, ORDER_NAME_PAIRS, ORDER_NEGOTIATION_DEFAULT, ORDER_SETTINGS_MSG as M, PAYMENT_NOTICE_LIMITS, PAYMENT_NOTICE_MSG, PAYMENT_NOTICE_NAMES, STANDARD_TERMS, orderAckNameFor, orderAckTurnOn, type OrderSettingsPage, type OrderSettingsUpdate } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, apiUpload, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupList, GroupNavRow, GroupSection, GroupSwitchRow, GroupValueRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { useModule } from "../core/slots.js";
import { t } from "../labels.js";

const KEY = ["company-order-settings"] as const;
const FREE = "__free__";

export function SettingsOrdersPage() {
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  // 約款・変更協議は建設の紙（field の会社＝現場力）だけ（#250。営業力の{order}はいつも仕入の紙）
  const field = useModule("field");
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<OrderSettingsPage>("GET", "/company/order-settings") });
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(t(M.title));
  const save = useMutation({
    mutationFn: (b: OrderSettingsUpdate) => api<OrderSettingsPage>("PUT", "/company/order-settings", b),
    onSuccess: (v, b) => {
      qc.setQueryData(KEY, v);
      if (b.order !== undefined || b.orderAck !== undefined || b.paymentNotice !== undefined) void qc.invalidateQueries({ queryKey: ME_KEY }); // 会社の言葉（メニューの名前）
      if (b.combined !== undefined) void qc.invalidateQueries({ queryKey: ["paper-defaults"] }); // 帳票デザインの会社の既定（同じ1つの値）
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const [ack, setAck] = useState("");
  const [freeName, setFreeName] = useState("");
  /** 変更協議の定型文の窓（#261。null＝閉じている） */
  const [nego, setNego] = useState<string | null>(null);
  const [days, setDays] = useState("");
  const [daysErr, setDaysErr] = useState<string | undefined>();
  const d = q.data;
  useEffect(() => {
    if (!d) return;
    setDays(String(d.settings.confirmDays));
    setAck(d.names.orderAck);
    setFreeName(ORDER_NAME_PAIRS.some((p) => p.order === d.names.order) ? "" : d.names.order);
  }, [d]);
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!d) return <p className="hint">読み込んでいます…</p>;
  const s = d.settings;
  const known = ORDER_NAME_PAIRS.some((p) => p.order === d.names.order);
  const pickName = (v: string) => {
    if (v === FREE) {
      setFreeName(d.names.order);
      return;
    }
    save.mutate({ order: v, orderAck: orderAckNameFor(v) });
  };
  /** 確認の期限（3〜30日）：欄を離れたときに保存。範囲の外は欄の下に朱の1行（保存しない） */
  const saveDays = () => {
    const v = Number(days.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).trim());
    if (!Number.isInteger(v) || v < PAYMENT_NOTICE_LIMITS.confirmDaysMin || v > PAYMENT_NOTICE_LIMITS.confirmDaysMax) {
      setDaysErr(PAYMENT_NOTICE_MSG.settingsConfirmDaysRange);
      return;
    }
    setDaysErr(undefined);
    setDays(String(v));
    if (v !== d.settings.confirmDays) save.mutate({ confirmDays: v });
  };
  const upload = async (f: File | null) => {
    if (!f) return;
    try {
      const r = await apiUpload<OrderSettingsPage>(`/company/order-terms?name=${encodeURIComponent(f.name)}`, f, "application/pdf");
      qc.setQueryData(KEY, r);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{t(M.title)}</h1>
        <span className="sp" />
        {save.isPending ? <span className="hint">保存中…</span> : save.isSuccess ? <span className="hint" data-testid="order-settings-saved">保存しました</span> : <span className="hint">自動保存</span>}
      </div>
      {!admin ? <p className="readonly-note">変更できるのは管理者のみです。</p> : null}
      <div data-testid="order-settings">
        <GroupSection title={M.names} help={<Help text={M.namesHelp} testId="help-order-names" />}>
          <GroupList>
            <GroupValueRow
              title={M.nameDoc}
              value={
                <select className="inp" aria-label={M.nameDoc} value={known && !freeName ? d.names.order : FREE} disabled={!admin} onChange={(e) => pickName(e.target.value)} data-testid="order-name-select">
                  {ORDER_NAME_PAIRS.map((p) => (
                    <option key={p.order} value={p.order}>{p.order}</option>
                  ))}
                  <option value={FREE}>{M.free}</option>
                </select>
              }
            />
            {freeName || !known ? (
              <GroupValueRow
                title={M.free}
                value={<input className="inp" aria-label={M.free} maxLength={12} value={freeName} disabled={!admin} onChange={(e) => setFreeName(e.target.value)} onBlur={() => freeName.trim() && freeName.trim() !== d.names.order && save.mutate({ order: freeName.trim(), orderAck: orderAckNameFor(freeName.trim()) })} data-testid="order-name-free" />}
              />
            ) : null}
            <GroupValueRow
              title={M.nameAck}
              value={<input className="inp" aria-label={M.nameAck} maxLength={12} value={ack} disabled={!admin} onChange={(e) => setAck(e.target.value)} onBlur={() => ack.trim() && ack.trim() !== d.names.orderAck && save.mutate({ orderAck: ack.trim() })} data-testid="order-ack-name" />}
            />
          </GroupList>
        </GroupSection>
        {/* {orderAck}（#256、Figma ①）：頭のスイッチ（既定オフ＝承諾なしで発行して完了）。オンのときだけ受け取り方・本人確認。兼請書の行はいつも出す */}
        <GroupSection title={t(M.ack)} help={<Help text={`${t(M.ackHelp)} ${t(M.combinedHelp)}`} testId="help-order-ack" />}>
          <GroupList>
            <GroupSwitchRow lead label={t(M.ack)} name={t(M.ack)} checked={s.ackMode !== "none"} disabled={!admin} onChange={(v) => save.mutate({ ackMode: v ? orderAckTurnOn(null) : "none" })} testId="order-ack-switch" rowTestId="row-order-ack" />
            {s.ackMode !== "none" ? (
              <GroupValueRow
                className="is-sub"
                title={M.ackReceive}
                titleId="so-ack-receive"
                value={
                  <Segmented
                    labelledBy="so-ack-receive"
                    value={s.ackMode}
                    disabled={!admin}
                    onChange={(v) => save.mutate({ ackMode: v })}
                    options={ORDER_ACK_RECEIVE.map((v) => ({ value: v, label: ORDER_ACK_LABEL[v], testId: `order-ack-receive-${v}` }))}
                  />
                }
                testId="row-order-ack-receive"
              />
            ) : null}
            {/* 書式（#233 2d）：{order}兼請書を既定に＝会社の既定の書式（設定 → 帳票デザイン と同じ1つの値）。請書のオン・オフに関わらず出す（#256）。
                説明は節の「？」（行は <label>＝中に「？」のボタンを置くと行を押したときにボタンが押される） */}
            <GroupSwitchRow className="is-sub" label={t(M.combined)} name={t(M.combined)} checked={d.combined} disabled={!admin} onChange={(v) => save.mutate({ combined: v })} testId="order-combined" rowTestId="row-order-combined" />
            {/* 本人確認（電子承諾のとき）：SMS の送り口がまだ無い＝リンクのみ（スイッチにしない＝docs/93 §17.3 ハ） */}
            {s.ackMode === "electronic" ? <GroupValueRow className="is-sub" title={M.identity} value={M.identityLink} valueTestId="order-identity" testId="row-order-identity" /> : null}
          </GroupList>
        </GroupSection>
        {/* 約款（#261、Figma ①）：頭のスイッチ（既定オフ＝添付しない）。オンのときだけ 種類・約款 PDF・印刷で約款・変更協議・定型文。field の会社だけ（#250） */}
        {field ? (
          <GroupSection title={M.terms} help={<Help text={`${t(M.termsHelp)} ${t(M.printTermsHelp)} ${M.negotiationHelp}`} testId="help-order-terms" />}>
            <GroupList>
              <GroupSwitchRow
                lead
                label={M.terms}
                name={M.terms}
                checked={s.termsKind !== "none"}
                disabled={!admin}
                onChange={(v) => save.mutate(v ? { termsKind: "standard", negotiationOn: true } : { termsKind: "none", negotiationOn: false })}
                testId="order-terms-switch"
                rowTestId="row-order-terms"
              />
              {s.termsKind !== "none" ? (
                <>
                  <GroupValueRow
                    className="is-sub"
                    title={M.termsKind}
                    titleId="so-terms-kind"
                    value={
                      <Segmented
                        labelledBy="so-terms-kind"
                        value={s.termsKind === "own" ? "own" : "standard"}
                        disabled={!admin}
                        onChange={(v) => save.mutate({ termsKind: v })}
                        options={[
                          { value: "standard" as const, label: M.termsStandard, testId: "order-terms-kind-standard" },
                          // 自社の PDF は PDF を登録してから（下の行の「PDF 選択」で登録すると自社の PDF になる）
                          { value: "own" as const, label: M.termsOwn, testId: "order-terms-kind-own", disabled: !s.termsAssetId },
                        ]}
                        testId="order-terms-kind"
                      />
                    }
                    testId="row-order-terms-kind"
                  />
                  <GroupValueRow
                    className="is-sub"
                    title={M.termsPdf}
                    value={
                      <span className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <a className="btn text sm" href="/api/v1/company/order-terms.pdf" target="_blank" rel="noreferrer" data-testid="order-terms-show">
                          {s.termsKind === "standard" ? STANDARD_TERMS.name : s.ownTermsName || d.termsFileName || M.termsOwn} ›
                        </a>
                        {admin ? (
                          <label className="btn sm" htmlFor="order-terms-file">
                            {M.termsOwnPick}
                            <input id="order-terms-file" type="file" accept="application/pdf,.pdf" className="file-hidden" onChange={(e) => void upload(e.target.files?.[0] ?? null)} data-testid="order-terms-file" />
                          </label>
                        ) : null}
                      </span>
                    }
                  />
                  <GroupSwitchRow className="is-sub" label={M.printTerms} name={M.printTerms} checked={s.printTerms} disabled={!admin} onChange={(v) => save.mutate({ printTerms: v })} testId="order-print-terms" rowTestId="row-order-print-terms" />
                  {/* 変更協議（#255・#261）：約款オンのときだけ。オンのときだけ定型文の行（押すと文の窓） */}
                  <GroupSwitchRow className="is-sub" label={M.negotiationOn} name={M.negotiationOn} checked={s.negotiationOn} disabled={!admin} onChange={(v) => save.mutate({ negotiationOn: v })} testId="order-negotiation-on" rowTestId="row-order-negotiation-on" />
                  {s.negotiationOn ? (
                    <GroupNavRow className="is-sub" title={M.negotiationText} value={s.negotiation} onClick={() => setNego(s.negotiation)} disabled={!admin} buttonTestId="order-negotiation-open" valueTestId="order-negotiation-text" />
                  ) : null}
                </>
              ) : null}
            </GroupList>
          </GroupSection>
        ) : null}
        <GroupSection title={M.print} help={<Help text={t(M.printHelp)} testId="help-order-print" />}>
          <GroupList>
            <GroupSwitchRow label="内訳表" name="内訳表" checked={s.printBreakdown} disabled={!admin} onChange={(v) => save.mutate({ printBreakdown: v })} testId="order-print-breakdown" />
            <GroupSwitchRow label="社印" name="社印" checked={s.printSeal} disabled={!admin} onChange={(v) => save.mutate({ printSeal: v })} testId="order-print-seal" />
          </GroupList>
        </GroupSection>
        {/* {paymentNotice}（#233 3b、docs/93 §15.6）：名称（支払通知書⇄支払明細書）と確認の期限（送付後 n日。3〜30・既定 7） */}
        <GroupSection title={t(PAYMENT_NOTICE_MSG.list)} help={<Help text={t(PAYMENT_NOTICE_MSG.settingsConfirmDaysHelp)} testId="help-order-notice" />} note={daysErr ? <span className="shu" data-testid="order-confirm-days-error">{daysErr}</span> : undefined}>
          <GroupList>
            <GroupValueRow
              title={PAYMENT_NOTICE_MSG.settingsName}
              value={
                <select className="inp" aria-label={`${t(PAYMENT_NOTICE_MSG.list)}の${PAYMENT_NOTICE_MSG.settingsName}`} value={d.names.paymentNotice} disabled={!admin} onChange={(e) => save.mutate({ paymentNotice: e.target.value })} data-testid="order-notice-name">
                  {(PAYMENT_NOTICE_NAMES as readonly string[]).includes(d.names.paymentNotice) ? null : <option value={d.names.paymentNotice}>{d.names.paymentNotice}</option>}
                  {PAYMENT_NOTICE_NAMES.map((x) => (
                    <option key={x} value={x}>{x}</option>
                  ))}
                </select>
              }
            />
            <GroupValueRow
              title={PAYMENT_NOTICE_MSG.settingsConfirmDays}
              value={
                <span className="row" style={{ gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                  <input className="inp num" style={{ width: "4.5em", textAlign: "right" }} type="text" inputMode="numeric" aria-label={PAYMENT_NOTICE_MSG.settingsConfirmDays} value={days} disabled={!admin} onChange={(e) => setDays(e.target.value)} onBlur={saveDays} data-testid="order-confirm-days" />
                  <span>{PAYMENT_NOTICE_MSG.settingsConfirmDaysUnit}</span>
                </span>
              }
            />
          </GroupList>
        </GroupSection>
      </div>
      {nego != null ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setNego(null)}>
          <div className="dlg" role="dialog" aria-label={M.negotiationText} onKeyDown={(e) => e.key === "Escape" && setNego(null)} data-testid="order-negotiation-dialog">
            <div className="dh">{M.negotiationText}</div>
            <div className="db">
              <textarea className="inp" rows={5} aria-label={M.negotiationText} value={nego} maxLength={1000} autoFocus onChange={(e) => setNego(e.target.value)} style={{ width: "100%" }} data-testid="order-negotiation" />
              {nego !== ORDER_NEGOTIATION_DEFAULT ? (
                <button type="button" className="btn text" onClick={() => setNego(ORDER_NEGOTIATION_DEFAULT)} data-testid="order-negotiation-reset">{M.negotiationReset}</button>
              ) : null}
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" onClick={() => setNego(null)}>取消</button>
              <button
                type="button"
                className="btn primary"
                disabled={!nego.trim()}
                onClick={() => {
                  if (nego.trim() !== s.negotiation) save.mutate({ negotiation: nego.trim() });
                  setNego(null);
                }}
                data-testid="order-negotiation-save"
              >
                決定
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {node}
    </>
  );
}
